import { useEffect } from 'react';
import {
  BrowserRouter as Router,
  HashRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import LicenseGate from './components/LicenseGate';
import { ROUTER_BASE } from './lib/assetUrl';
import { register } from './lib/license';
import { needsOnboarding } from './lib/onboarding';
import HomePage from './pages/HomePage';
import OnboardingPage from './pages/OnboardingPage';
import CoursePage from './pages/CoursePage';
import ProfilePage from './pages/ProfilePage';
import TranslatePage from './pages/TranslatePage';
import ConversationPage from './pages/ConversationPage';
import SavedPhrasesPage from './pages/SavedPhrasesPage';
import LessonPage from './pages/LessonPage';
import LessonSessionPage from './pages/LessonSessionPage';
import GenerateCoursePage from './pages/GenerateCoursePage';
import DialectSelectPage from './pages/DialectSelectPage';
import RadioPage from './pages/RadioPage';
import ReviewPage from './pages/ReviewPage';

/**
 * 方言通 · DialectGo —— 路由表
 * ============================================================
 * 从原项目（Learn-Cantonese）抽出的**只有课程闭环**。
 *
 * 原项目路由表里有 30 多个页面，混着拍照识别、故事、俚语、电台、
 * 好友、二维码、分享等七八个互不相关的模块。这里只保留：
 *
 *   /                      首页（继续学习 / 复习提醒 / 快捷入口）
 *   /courses               课程地图（按等级分组）
 *   /profile               我的（本机数据概览与清理）
 *   /translate             翻译（普通话 → 方言 + 跟读）
 *   /conversation          场景对话（虚拟人老师，含水平自适应）
 *   /lesson/:lessonId      单课详情 / 课后复习
 *   /session/:lessonId     引导式课堂（主学习路径）
 *   /generate              课程生成器
 *   /review                复习队列（按遗忘曲线抽已学句型）
 *
 * 没有带过来的功能及原因：
 *   /camera /story /library /practice /slang /radio
 *       —— 与"课程"无关的独立功能
 *   /chat（口语）
 *       —— 自由对话页，约 1500 行，是独立产品形态；
 *          课堂里的 dialogue 步骤已经覆盖了"进场景开口"的需求
 *   /friends /share-story /qr-scan /notifications /profile
 *       —— 社交与账户体系
 */
/**
 * 首次启动跳转
 *
 * 只在**用户还没跑完引导**时跳一次，跑过之后不再打扰。
 * 判断放在渲染期而不是 useEffect：用 effect 会先闪一下首页再跳走。
 */
function FirstRunGate({ children }) {
  const { pathname } = useLocation();
  const onOnboarding = pathname.startsWith('/onboarding');
  if (!onOnboarding && needsOnboarding()) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function AppShell({ Router, basename }) {
  /*
   * 启动时向服务端注册设备（幂等）。
   *
   * 首次调用会开启 14 天试用；之后每次只是刷新活跃时间，
   * **不会**重置试用期 —— 否则用户重装一次就能重新白嫖。
   * 失败不阻断启动：服务端没启用授权、或暂时连不上，都应当能正常用。
   */
  useEffect(() => {
    register().catch(() => {});
  }, []);

  return (
    /*
     * future 开关：提前采用 React Router v7 的两项行为，消除控制台里的
     * 「React Router Future Flag Warning」。
     *
     *   v7_startTransition    把状态更新包进 startTransition，可减少切换卡顿。
     *                         本项目没有用 React.lazy / Suspense，风险很低。
     *   v7_relativeSplatPath  改变 splat 路由内的相对路径解析。
     *                         本项目没有任何 splat 路由，属于空操作。
     *
     * 将来若引入 lazy 加载或 splat 路由，需要重新评估。
     */
    <Router
      /*
       * basename 只在 BrowserRouter 下需要。
       *
       * BrowserRouter：路径取自 location.pathname。部署在子目录
       * （如 /dialectgo/）时必须设 basename=/dialectgo，否则
       * `<Link to="/courses">` 会跳到域名根的 /courses，跳出应用。
       *
       * HashRouter：路径取自 location.hash 内部（`#/courses` 里的 `/courses`），
       * 这段**本来就不含目录前缀** —— 再传 basename=/dialectgo 会要求
       * hash 里的路径也以 /dialectgo 开头，于是 `#/` 匹配不上任何路由，
       * 整页渲染为空白（实测踩过：手机扫码打开 /dialectgo/ 全白，
       * 而手工加 /#/dialectgo/ 反而正常）。所以 hash 模式下必须传 '/'。
       */
      basename={basename}
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      {/*
        全局手机宽度容器。
        这是移动端应用：桌面上应以手机宽度居中呈现，而不是把内容拉伸铺满。
        此前各页面都没有宽度约束，教学页的 4:3 立绘在 1900px 宽的窗口下
        会被撑到上千像素高 —— 根因就在这里。
      */}
      <div className="mx-auto min-h-screen w-full max-w-md bg-[#fdfaf1] shadow-2xl dark:bg-zinc-900">
      <FirstRunGate>
        <Routes>
          {/* 一级导航对齐需求文档 V1.0：首页 / 学习 / 发现 / 我的 */}
        <Route path="/" element={<HomePage />} />
        <Route path="/courses" element={<CoursePage />} />
        <Route path="/profile" element={<ProfilePage />} />
        {/* 首次启动引导（需求文档 A 组） */}
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/lesson/:lessonId" element={<LessonPage />} />
        <Route path="/session/:lessonId" element={<LessonSessionPage />} />
        <Route path="/generate" element={<GenerateCoursePage />} />
        {/* 方言选择：切换当前学习的方言（决定课堂里听到什么声音） */}
        {/* 翻译：普通话 → 方言 + 跟读（需求文档 D 组） */}
        {/* 场景对话：兴趣采集 → 场景 → 词汇 → 方言对话（含水平自适应与跑题处理） */}
        <Route path="/conversation" element={<ConversationPage />} />
        <Route path="/translate" element={<TranslatePage />} />
        {/* 常用句收藏：离线可查（音频首次播放后缓存到 IndexedDB） */}
        <Route path="/saved" element={<SavedPhrasesPage />} />
        <Route path="/dialects" element={<DialectSelectPage />} />
        <Route path="/radio" element={<RadioPage />} />
        <Route path="/review" element={<ReviewPage />} />
        </Routes>
      </FirstRunGate>
      {/* 额度/授权拦截：任何页面的 402 都由它统一接住 */}
      <LicenseGate />
      </div>
    </Router>
  );
}

/*
 * 云版构建（VITE_ROUTER=hash）用 HashRouter，本地/自托管版用 BrowserRouter。
 * 见 AppShell 里 <Router> 处的注释：为什么云版必须用 hash 路由（刷新 404 问题）。
 */
const USE_HASH = import.meta.env.VITE_ROUTER === 'hash';

export default function App() {
  const R = USE_HASH ? HashRouter : Router;
  /*
   * hash 模式传 '/'（不加目录前缀），browser 模式传真实 base。
   * 见 AppShell 里 <Router> 处的注释：hash 内的路径不含子目录前缀，
   * 多传 basename 会让 `#/` 匹配不上任何路由 → 整页空白。
   */
  return <AppShell Router={R} basename={USE_HASH ? '/' : ROUTER_BASE} />;
}
